/**
 * BrightPath - TaxSummaryCard Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { Receipt, Download, TrendingUp, Info } from 'lucide-react';
import type { TaxReport } from '../types';
import { formatCurrency } from '@/core/lib/utils';

interface TaxSummaryCardProps {
  report: TaxReport;
  onExport?: () => void;
  isExporting?: boolean;
}

export function TaxSummaryCard({ report, onExport, isExporting = false }: TaxSummaryCardProps) {
  return (
    <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-warm-gray/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-sage" />
          </div>
          <div>
            <h3 className="font-semibold text-navy">Tax Summary</h3>
            <p className="text-sm text-warm-gray">Tax Year {report.year}</p>
          </div>
        </div>
        {onExport && (
          <button
            onClick={onExport}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        )}
      </div>

      {/* Main Stats */}
      <div className="p-5">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-sage/10 rounded-lg">
            <p className="text-sm text-warm-gray mb-1">Tax Deductible</p>
            <p className="text-2xl font-bold text-sage">
              {formatCurrency(report.totalDeductible)}
            </p>
          </div>
          <div className="p-4 bg-cream rounded-lg">
            <p className="text-sm text-warm-gray mb-1">Non-Deductible</p>
            <p className="text-2xl font-bold text-navy">
              {formatCurrency(report.totalNonDeductible)}
            </p>
          </div>
        </div>

        {/* Estimated Deduction */}
        {report.summary && (
          <div className="p-4 bg-terracotta/10 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-terracotta mt-0.5" />
              <div>
                <p className="font-medium text-navy">Estimated Deduction</p>
                <p className="text-2xl font-bold text-terracotta">
                  {formatCurrency(report.summary.estimatedDeduction)}
                </p>
                <p className="text-sm text-warm-gray mt-1">
                  Based on medical expenses exceeding {formatCurrency(report.summary.medicalThreshold)} (7.5% AGI threshold)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        <div>
          <h4 className="font-medium text-navy mb-3">By Category</h4>
          <div className="space-y-2">
            {report.byCategory.map((cat) => (
              <div
                key={`${cat.category}-${cat.taxCategory}`}
                className="flex items-center justify-between py-2 border-b border-warm-gray/10 last:border-0"
              >
                <div>
                  <p className="font-medium text-navy">{cat.taxCategory}</p>
                  <p className="text-xs text-warm-gray">{cat.itemCount} items</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-navy">{formatCurrency(cat.deductible)}</p>
                  {cat.nonDeductible > 0 && (
                    <p className="text-xs text-warm-gray">
                      +{formatCurrency(cat.nonDeductible)} non-deductible
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {report.summary?.notes && report.summary.notes.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-medium text-navy mb-1">Notes</p>
                <ul className="text-sm text-warm-gray space-y-1">
                  {report.summary.notes.map((note, i) => (
                    <li key={i}>• {note}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TaxSummaryCard;
